// @ts-check
import * as React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'hooks'
import { withTranslations } from 'helpers'
import SizesGrid from './SizesGrid'

/**
 * Only used on `products_controller#stock_notification` path
 * @param {{
 * allSizeConversions: import('types/sizeConversion').SizeConversion[];
 * selectedSizeConversionIds: number[];
 * sizeConversionIdsAndPrices: { size_conversion_id: number }[];
 * }} props
 */
const StockSizeOptions = ({
  allSizeConversions,
  selectedSizeConversionIds: initialSelectedSizeConversionIds,
  sizeConversionIdsAndPrices,
  groupType
}) => {
  const { t } = useTranslation()
  // Create an array of the IDs of the size conversions that are in stock
  const inStockSizeConversionIds = React.useMemo(
    () =>
      sizeConversionIdsAndPrices.map(({ size_conversion_id: scId }) => scId),
    [sizeConversionIdsAndPrices]
  )

  // Create an array of the IDs of the size conversions that are out of stock
  const outOfStockSizeConversionIds = React.useMemo(
    () =>
      allSizeConversions
        .filter(({ id }) => !inStockSizeConversionIds.includes(id)) // Only include size conversions that are not in stock
        .map(({ id }) => id), // Map the array to only include the ID of each size conversion
    [allSizeConversions, inStockSizeConversionIds]
  )
  const [selectedSizeConversionIds, setSelectedSizeConversionIds] =
    React.useState(initialSelectedSizeConversionIds)

  /**
   * Toggle the selected state of a size conversion
   * @param {{ id: number }} sizeConversion - The size conversion to toggle
   */
  const toggleSizeConversionSelected = (sizeConversion) => {
    if (!outOfStockSizeConversionIds.includes(sizeConversion.id)) return

    if (selectedSizeConversionIds.includes(sizeConversion.id)) {
      setSelectedSizeConversionIds(
        selectedSizeConversionIds.filter((s) => s !== sizeConversion.id)
      )
    } else {
      setSelectedSizeConversionIds([
        ...selectedSizeConversionIds,
        sizeConversion.id
      ])
    }
  }

  return (
    <SizesGrid
      disabledTooltip={t(`react_components.product.${groupType}.is_in_stock`)}
      groupType={groupType}
      onSizeConversionSelected={toggleSizeConversionSelected}
      isStockNotificationSizeOption
      sizeConversions={allSizeConversions.map((sizeConversion) => ({
        ...sizeConversion,
        active: selectedSizeConversionIds.includes(sizeConversion.id),
        disabled: !outOfStockSizeConversionIds.includes(sizeConversion.id)
      }))}
    />
  )
}

StockSizeOptions.propTypes = {
  allSizeConversions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired
    })
  ).isRequired,
  selectedSizeConversionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  sizeConversionIdsAndPrices: PropTypes.arrayOf(
    PropTypes.shape({
      size_conversion_id: PropTypes.number.isRequired
    })
  ).isRequired
}

export default withTranslations(StockSizeOptions)
